import React, { useState } from "react";

//UI Material
import {
  Button,
  Checkbox,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  useMediaQuery,
} from "@mui/material";

//Helpers
import {
  typographyForCustomizedFontSize,
  typographyForSubtitles,
  typographyForText,
} from "../../Helpers/typography";
import { fetchWithTokenJSONPost } from "../../Helpers/network";

//Local components
import ContainedButton from "../basics/ContainedButton";
import SerialNumberDrawer from "../drawers/SerialNumberDrawer";
import ComModDrawer from "../drawers/ComModDrawer";

//Material Icons
import {
  ArrowForwardIos,
  Edit,
  SubdirectoryArrowRight,
} from "@mui/icons-material";

//Helpers
import { generateStatusName } from "../../Helpers/orderFunctions";
import { generateChipForOrderStatus } from "../../Helpers/orderFunctions";

export default function ProducedItemRow({
  products,
  getOrderData,
  canceled,
  productInfo,
  bulkSelectionEnabled = false,
  selectedProductIds = [],
  onToggleProduct,
  bulkFinishing = false,
}) {
  // ==========> Component States<==========
  //State for open/close drawer for serial number assign
  const [serialDrawerOpen, setSerialDrawerOpen] = useState(false);
  //State for open/close drawer for comMod number assign
  const [comModDrawerOpen, setComModDrawerOpen] = useState(false);
  //State for set product data
  const [selectedData, setSelectedData] = useState({});
  //State for loading when finishing a product
  const [finishingProductId, setFinishingProductId] = useState(null);
  //Extracted userData from localstorage
  const user = JSON.parse(localStorage.getItem("userData"));
  //Defines the min width screensize to phoneSize
  const phoneSize = !useMediaQuery("(min-width:600px");

  // ==========> Component Functions<==========
  //Function to finish a product
  const finishProduct = async (productId) => {
    setFinishingProductId(productId);
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        "/interactions/create-product-interaction",
        {
          productId: productId,
          action: "PRODUCT_FINISHED",
        }
      );
      const jsonResponse = await requestResponse.json();
      if (jsonResponse.status === "ok") {
        getOrderData();
      }
    } catch (error) {
      console.log(error);
    }
    setTimeout(() => {
      setFinishingProductId(null);
    }, 1400);
  };

  //Function to select the type of row according to screen size
  const selectRowType = (product) => {
    const isProductSelectable =
      bulkSelectionEnabled && product.status !== "FINISHED" && !canceled;
    const isProductSelected = selectedProductIds.includes(product.productId);

    if (phoneSize) {
      return (
        <TableRow key={product.productId}>
          <SerialNumberDrawer
            open={serialDrawerOpen}
            setOpen={setSerialDrawerOpen}
            product={selectedData}
            getData={getOrderData}
          />
          <ComModDrawer
            open={comModDrawerOpen}
            setOpen={setComModDrawerOpen}
            product={selectedData}
            getData={getOrderData}
          />
          <TableCell>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              justifyContent="space-between"
            >
              {bulkSelectionEnabled && (
                <Checkbox
                  size="small"
                  checked={isProductSelected}
                  disabled={!isProductSelectable || bulkFinishing}
                  onChange={() => onToggleProduct(product.productId)}
                  inputProps={{ "aria-label": `Seleccionar ${product.productId}` }}
                />
              )}
              <Stack spacing={0.5} alignItems="flex-start" sx={{ flex: 1 }}>
                {typographyForSubtitles(
                  `${product.aditionalData.productName} ${product.aditionalData.powerConfigType || ""
                  }`
                )}
                <Stack direction="row" spacing={1}>
                  {typographyForText(product.productId, "bold")}
                  {typographyForText(
                    canceled ? "CANCELADO" : generateStatusName(product.status)
                  )}
                </Stack>

                {typographyForText(
                  product.specifications || "Sin especificaciones"
                )}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ marginTop: "5px" }}
                >
                  {product.aditionalData.serial === "Sin asignar" ? (
                    <Button
                      endIcon={<Edit />}
                      size="small"
                      onClick={() => {
                        setSelectedData(product);
                        setSerialDrawerOpen(true);
                      }}
                      variant="outlined"
                    >
                      S/N
                    </Button>
                  ) : (
                    typographyForCustomizedFontSize(
                      `S/N: ${product.aditionalData.serial}`,
                      "12.5px"
                    )
                  )}
                  {product.aditionalData.comMod ? (
                    product.aditionalData.comMod === "Sin asignar" ? (
                      <Button
                        endIcon={<Edit />}
                        size="small"
                        onClick={() => {
                          setSelectedData(product);
                          setComModDrawerOpen(true);
                        }}
                        variant="outlined"
                      >
                        Commod
                      </Button>
                    ) : (
                      typographyForCustomizedFontSize(
                        `ComMod: ${product.aditionalData.comMod}`,
                        "12.5px"
                      )
                    )
                  ) : (
                    typographyForCustomizedFontSize(
                      `ComMod: ${product.aditionalData.comMod}`,
                      "12.5px"
                    )
                  )}
                  {productInfo?.nonRequiredQC === true && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        sx={{ color: "white", fontSize: "11px" }}
                        disabled={
                          product.status === "FINISHED" ||
                          canceled ||
                          finishingProductId === product.productId
                        }
                        onClick={() => {
                          finishProduct(product.productId);
                        }}
                      >
                        {finishingProductId === product.productId
                          ? "Finalizando..."
                          : "Finalizar"}
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        sx={{ color: "white", fontSize: "11px" }}
                        endIcon={<ArrowForwardIos />}
                        onClick={() => {
                          window.location.href = `/produced-item?productId=${product.productId}`;
                        }}
                      >
                        Abrir
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Stack>
              {!productInfo?.nonRequiredQC && (
                <IconButton
                  size="small"
                  onClick={() => {
                    window.location.href = `/produced-item?productId=${product.productId}`;
                  }}
                >
                  <ArrowForwardIos />
                </IconButton>
              )}
            </Stack>
          </TableCell>
        </TableRow>
      );
    } else {
      return (
        <TableRow key={product.productId}>
          <SerialNumberDrawer
            open={serialDrawerOpen}
            setOpen={setSerialDrawerOpen}
            product={selectedData}
            getData={getOrderData}
          />
          <ComModDrawer
            open={comModDrawerOpen}
            setOpen={setComModDrawerOpen}
            product={selectedData}
            getData={getOrderData}
          />
          {bulkSelectionEnabled && (
            <TableCell padding="checkbox" align="center">
              <Checkbox
                size="small"
                checked={isProductSelected}
                disabled={!isProductSelectable || bulkFinishing}
                onChange={() => onToggleProduct(product.productId)}
                inputProps={{ "aria-label": `Seleccionar ${product.productId}` }}
              />
            </TableCell>
          )}
          <TableCell align="center">
            {typographyForText(product.productId)}
          </TableCell>
          <TableCell align="center">
            <Stack alignItems="center">
              {typographyForText(
                `${product.aditionalData.productName} ${product.aditionalData.powerConfigType || ""
                }`,
                "bold"
              )}
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                alignItems="center"
                textAlign="left"
                sx={{ maxWidth: "200px" }}
              >
                <SubdirectoryArrowRight fontSize="small" />
                {typographyForCustomizedFontSize(
                  product.specifications || "Sin especificaciones",
                  "11.7px"
                )}
              </Stack>
            </Stack>
          </TableCell>
          <TableCell align="center">
            {product.aditionalData.serial === "Sin asignar" ? (
              <Button
                variant="outlined"
                size="small"
                endIcon={<Edit />}
                sx={{ fontSize: "11px" }}
                onClick={() => {
                  setSelectedData(product);
                  setSerialDrawerOpen(true);
                }}
              >
                AGREGAR
              </Button>
            ) : (
              typographyForText(product.aditionalData.serial)
            )}
          </TableCell>
          {product.aditionalData.comMod ? (
            <TableCell align="center">
              {product.aditionalData.comMod === "Sin asignar" ? (
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<Edit />}
                  sx={{ fontSize: "11px" }}
                  onClick={() => {
                    setSelectedData(product);
                    setComModDrawerOpen(true);
                  }}
                >
                  AGREGAR
                </Button>
              ) : (
                typographyForText(product.aditionalData.comMod)
              )}
            </TableCell>
          ) : (
            <TableCell align="center">{typographyForText("N/A")}</TableCell>
          )}

          <TableCell align="center">
            {generateChipForOrderStatus(
              canceled ? "cancelada" : generateStatusName(product.status)
            )}
          </TableCell>
          {productInfo?.nonRequiredQC === true ? (
            <TableCell align="center">
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  sx={{ color: "white" }}
                  disabled={
                    product.status === "FINISHED" ||
                    canceled ||
                    finishingProductId === product.productId
                  }
                  onClick={() => {
                    finishProduct(product.productId);
                  }}
                >
                  {finishingProductId === product.productId
                    ? "Finalizando..."
                    : "Finalizar"}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  sx={{ color: "white" }}
                  endIcon={<ArrowForwardIos />}
                  onClick={() => {
                    window.location.href = `/produced-item?productId=${product.productId}`;
                  }}
                >
                  Abrir
                </Button>
              </Stack>
            </TableCell>
          ) : (
            <TableCell align="center">
              <ContainedButton
                text="abrir"
                variant={
                  canceled && user.role !== "ADMINISTRATOR"
                    ? "disabled"
                    : "contained"
                }
                endIcon={<ArrowForwardIos />}
                onClick={() => {
                  window.location.href = `/produced-item?productId=${product.productId}`;
                }}
              />
            </TableCell>
          )}

        </TableRow>
      );
    }
  };

  return products.map((product) => selectRowType(product));
}
